import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';

import { paymentsShape } from 'constants/propTypes';
import { COVID_VOUCHER_REFUND } from 'constants/payment';
import { FormattedMessage } from 'react-intl';

import { CurrencyInput } from 'components/ui/Inputs';
import CardSelect from 'components/ui/CardSelect';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import Button from 'components/ui/Button';

import { ADD_NOTE_TO_BOOKING } from 'store/mutations';
import { useMutation } from '@apollo/client';

import { ErrorMessage, FormWrapper, Spacer, ProviderLabel, AmountLabel } from '../CardRefund/CardRefund.styles';
import PaymentAgeWarning from '../PaymentAgeWarning';

const SocialMediaRefund = ({
  totalPriceToBePaid,
  totalAvailableRefundAmount,
  processCardPaymentRefund,
  payments,
  paymentLocation,
  bookingReference,
  currency,
  isCancelVoucher,
  mainBookingRef,
  hasHotel
}) => {
  const [addNote] = useMutation(ADD_NOTE_TO_BOOKING);

  const defaultRefundAmount = Number(Math.min(totalPriceToBePaid, totalAvailableRefundAmount).toFixed(2));

  const { register, handleSubmit, clearErrors, formState, watch } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const { errors } = formState;
  const selectedReference = watch('reference');

  const [isDisabledRefund, setDisabledRefund] = useState(false);
  const [maxAmount, setMaxAmount] = useState(defaultRefundAmount);
  const [selectedAmount, setSelectedAmount] = useState(defaultRefundAmount);

  const onSubmit = async ({ amount, reference: pspReference }) => {
    try {
      await processCardPaymentRefund({
        variables: {
          bookingReference,
          refundAmount: parseFloat(amount),
          pspReference,
          currency,
          paymentLocation,
          hasHotel
        }
      });

      if (isCancelVoucher) {
        addNote({
          variables: {
            note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
            bookingReference: mainBookingRef
          }
        });

        addNote({
          variables: {
            note: `${COVID_VOUCHER_REFUND}, main booking: ${mainBookingRef}, extra only booking: ${bookingReference}`,
            bookingReference
          }
        });
      }
    } catch (err) {
      return err;
    }
  };

  const hasReferenceError = Boolean(errors.reference);
  const hasAmountError = Boolean(errors.amount);

  if (totalAvailableRefundAmount === 0) {
    return (
      <p data-cy="noRefundLeft">
        <FormattedMessage id="refunds.card.noRefundLeftOnMethod" />
      </p>
    );
  }

  return (
    <form data-testid="social-media-refund-form" data-cy="refund-form" onSubmit={handleSubmit(onSubmit)}>
      <FormWrapper>
        <header>
          <h2 data-cy="title">
            <FormattedMessage id="payments.AmountToRefundBySocial_Media" />
          </h2>
          <p data-cy="paragraph">
            <FormattedMessage id="refunds.canBeSplit" />
          </p>
        </header>
        <Spacer>
          <CurrencyInput
            currency={currency}
            dataCy="amount-input"
            dataTestId="amount-input"
            defaultValue={defaultRefundAmount}
            isControlled={false}
            {...register('amount', {
              required: true,
              min: 0.01,
              max: defaultRefundAmount,
              onChange: (e) => {
                setSelectedAmount(e.target.value);
                setDisabledRefund(e.target.value > maxAmount || e.target.value > defaultRefundAmount);
              }
            })}
            step={0.01}
          />
        </Spacer>
        <h3>
          <FormattedMessage id="refunds.card.selectCard" />
        </h3>
        {payments.map((payment) => (
          <CardSelect
            key={payment.refId}
            id={payment.refId}
            testId={payment.refId}
            value={payment.reference}
            onClick={() => {
              setMaxAmount(payment.amount);
              setDisabledRefund(selectedAmount > payment.amount);
            }}
            {...register('reference', { required: true, onChange: () => clearErrors('reference') })}
            disabled={selectedAmount > payment.amount || currency !== payment.currency}
          >
            <div>
              <span>{payment.reference}</span>
              <ProviderLabel data-test={`card-${payment.refId}-provider`}>adyen</ProviderLabel>
            </div>
            <div>
              <AmountLabel>
                <FormattedMessage id="refunds.original" />
              </AmountLabel>
              <FormattedCurrency
                dataCy={`original-amount-${payment.reference}`}
                dataTestId={`original-amount-${payment.reference}`}
                currency={payment.currency}
                amount={payment?.amountInOriginalCurrency || payment?.amount || 0}
              />
            </div>
            <div>
              <AmountLabel>
                <FormattedMessage id="refunds.available" />
              </AmountLabel>
              <FormattedCurrency
                dataCy={`available-amount-${payment.reference}`}
                dataTestId={`available-amount-${payment.reference}`}
                currency={payment.currency}
                amount={payment?.availableRefundAmount || 0}
              />
            </div>
          </CardSelect>
        ))}
        {hasAmountError && (
          <ErrorMessage data-cy="amount-error-msg">
            <FormattedMessage
              id="refunds.card.error.amount"
              values={{
                amount: <FormattedCurrency currency={currency} amount={defaultRefundAmount} />
              }}
            />
          </ErrorMessage>
        )}
        {hasReferenceError && (
          <ErrorMessage data-cy="reference-error-msg">
            <FormattedMessage id="refunds.card.error.selectCard" />
          </ErrorMessage>
        )}
        {selectedAmount > maxAmount && (
          <ErrorMessage data-cy="max-amount-error-msg">
            <FormattedMessage id="refunds.socialMedia.error.maxamount" />
          </ErrorMessage>
        )}
        <PaymentAgeWarning
          payment={payments.find((payment) => payment.reference === selectedReference)}
          amount={selectedAmount}
          maxAmount={Math.min(maxAmount, defaultRefundAmount)}
          hasError={hasAmountError || hasReferenceError || isDisabledRefund}
        />
        <Button data-testid="submit" data-cy="submit" type="submit" disabled={isDisabledRefund} variant="primary">
          <FormattedMessage id="refunds.card.submit" />
        </Button>
      </FormWrapper>
    </form>
  );
};

SocialMediaRefund.propTypes = {
  bookingReference: PropTypes.string,
  totalPriceToBePaid: PropTypes.number,
  totalAvailableRefundAmount: PropTypes.number,
  payments: paymentsShape,
  processCardPaymentRefund: PropTypes.func,
  paymentLocation: PropTypes.string,
  currency: PropTypes.string,
  isCancelVoucher: PropTypes.bool,
  mainBookingRef: PropTypes.string,
  hasHotel: PropTypes.bool
};

export default SocialMediaRefund;
